<template>
  <div class="row">
    <!-- User Card -->
    <div class="col-xl-4 col-lg-5 col-md-6">
      <div class="iphone-x">
        <div v-if="!iframeLoaded" class="progress-container">
          <div class="progress">
            <div class="progress-bar" role="progressbar" :style="{ width: loadingProgress + '%' }" :aria-valuenow="loadingProgress" aria-valuemin="0" aria-valuemax="100">
              {{ loadingProgress }}%
            </div>
          </div>
        </div>
        <div class="iframe-container" v-show="iframeLoaded">
          <iframe :src="qrUserUrl" title="User QR Profile" @load="onIframeLoad" ref="userIframe"></iframe>
        </div>
      </div>
    </div>

    <!-- Edit Profile Section -->
    <div class="col-xl-8 col-lg-7 col-md-6">
      <card class="card" :title="$t('profile_social_edit.editSocialProfile')">
        <form>
          <!-- Card Selection and Input Fields -->
          <div class="row">
            <!-- Card Selector -->
            <div class="col-xl-3 col-lg-3 col-md-6 col-sm-12 mb-3">
              <label>{{ $t('profile_social_edit.selectCard') }}</label>
              <div class="d-flex align-items-center">
                <img v-if="selectedCardItem" style="width: 25px" :src="getCardIconSrc(selectedCardItem)" class="mr-2"/>
                <v-select :options="cards" label="name" v-model="selectedCardItem" class="flex-grow-1" @input="handleCardSelection">
                  <template slot="option" slot-scope="option">
                    <img class="col-4" :src="getCardIconSrc(option)"/>
                    {{ option.name }}
                  </template>
                </v-select>
              </div>
            </div>

            <!-- Title Input -->
            <div class="col-xl-4 col-lg-4 col-md-6 col-sm-12 mb-3">
              <fg-input
                type="text"
                :label="$t('profile_social_edit.title')"
                :placeholder="$t('profile_social_edit.titlePlaceholder')"
                v-model="cardTitle"
              />
            </div>

            <!-- Address Input -->
            <div v-if="showAddressInput" class="col-xl-4 col-lg-4 col-md-6 col-sm-12 mb-3">
              <fg-input
                type="text"
                :label="$t('profile_social_edit.address')"
                :placeholder="$t('profile_social_edit.addressPlaceholder')"
                v-model="cardAddress"
                :disabled="isAddressInputDisabled"
              />
            </div>

            <!-- Custom Icon Uploader -->
            <div v-if="isCustomCard" class="row align-items-center">
              <div class="col-10">
                <fg-input
                  v-if="!customIconFile"
                  type="file"
                  :label="$t('profile_social_edit.uploadCustomIcon')"
                  accept=".jpg, .png, .jpeg"
                  @change.native.prevent="handleCustomIconUpload"
                  :placeholder="$t('profile_social_edit.selectFile')"
                />
                <fg-input
                  v-else
                  type="text"
                  :label="$t('profile_social_edit.uploadCustomIcon')"
                  :value="customIconFile.name"
                  disabled
                />
              </div>
              <div class="col-2" v-if="customIconFile">
                <p-button
                  type="danger"
                  round
                  @click.native.prevent="removeCustomIcon"
                  class="text-center ti-trash"
                  style="position: relative; z-index: 10;"
                ></p-button>
              </div>
            </div>

            <!-- Catalog Uploader -->
            <div v-if="isCatalogCard" class="row align-items-center">
              <div class="col-10">
                <fg-input
                  v-if="!catalogFile"
                  type="file"
                  :label="$t('profile_social_edit.uploadCatalog')"
                  accept=".pdf"
                  @change.native.prevent="handleCatalogSelection"
                  :placeholder="$t('profile_social_edit.selectFile')"
                />
                <fg-input
                  v-else
                  type="text"
                  :label="$t('profile_social_edit.uploadCatalog')"
                  :value="catalogFile.name"
                  disabled
                />
              </div>
              <div class="col-2" v-if="catalogFile">
                <p-button
                  type="danger"
                  round
                  @click.native.prevent="removeCatalog"
                  class="text-center ti-trash"
                  style="position: relative; z-index: 10;"
                ></p-button>
              </div>
            </div>


          </div>

          <!-- Upload Progress -->
          <div v-if="isUploading" class="progress mt-3">
            <div class="progress-bar" role="progressbar" :style="{ width: uploadProgress + '%' }" :aria-valuenow="uploadProgress" aria-valuemin="0" aria-valuemax="100">{{uploadProgress}}%</div>
          </div>
          <!-- Add/Save Card Button -->
          <div class="col-xl-1 col-lg-1 col-md-12 col-sm-12 mb-3 d-flex align-items-end justify-content-center">
            <p-button type="info" round @click.native.prevent="addOrUpdateCard" class="text-center"
                      :class="editing_index != null ? 'ti-save' : 'ti-plus'"></p-button>
          </div>
          <!-- Cards Table -->
          <div class="row">
            <div class="col-12">
              <card class="card-plain">
                <table class="table table-striped tableClass">
                  <tbody>
                  <tr v-for="(item, index) in userCards" :key="index">
                    <td><img style="width: 25px" :src="getCardIconSrc(item)"/></td>
                    <td>{{ item.title }}</td>
                    <td style="max-width: 400px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">{{ item.address }}</td>
                    <td>
                      <p-button type="info" round @click.native.prevent="editCards(index)" class="text-center ti-pencil-alt"></p-button>
                    </td>
                    <td>
                      <p-button type="danger" round @click.native.prevent="removeCards(index)" class="text-center ti-trash"></p-button>
                    </td>
                  </tr>
                  </tbody>
                </table>
              </card>
            </div>
          </div>
        </form>

        <!-- Update Profile Button -->
        <div class="text-center">
          <p-button type="info" round @click.native.prevent="updateProfile">
            {{ $t('profile_social_edit.update') }}
          </p-button>
        </div>
      </card>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import config from "@/routing_map.json";
import router from "@/router";
import NotificationTemplate from "@/pages/Notifications/NotificationTemplate";
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import io from 'socket.io-client';

export default {
  components: {
    vSelect
  },
  data() {
    return {
      user: {
        company: "",
        name: "",
        surname: "",
        address: "",
        title: "",
      },
      userCards: [],
      cards: [],
      editing_index: null,
      cardTitle: null,
      cardAddress: null,
      profileImageSrc: null,
      backgroundImageSrc: null,
      imageUrl: null,
      selectedCardItem: null,
      customIconFile: null,
      maxImageSize: 5 * 1024 * 1024, // 5MB in bytes
      socket: null,
      uploadProgress: 0,
      isUploading: false,
      catalogFile: null,
      qrUserUrl:"onlinecardvisit.com",
      iframeLoaded: false,
      loadingProgress: 0,
      loadingInterval: null,
    };
  },
  computed: {
    isCustomCard() {
      return this.selectedCardItem && this.selectedCardItem.name === 'custom';
    },
    isCatalogCard() {
      return this.selectedCardItem && this.selectedCardItem.name === 'catalog';
    },
    showAddressInput() {
      return this.selectedCardItem && !this.isCatalogCard;
    },
    isAddressInputDisabled() {
      return this.isCatalogCard;
    }
  },
  mounted() {
    this.getUser();
    this.getCards();
    this.initializeSocket();
    this.startLoadingAnimation();
  },
  methods: {
    startLoadingAnimation() {
      this.loadingProgress = 0;
      this.loadingInterval = setInterval(() => {
        if (this.loadingProgress < 90) {
          this.loadingProgress += 10;
        }
      }, 200);
    },

    onIframeLoad() {
      clearInterval(this.loadingInterval);
      this.loadingProgress = 100;
      setTimeout(() => {
        this.iframeLoaded = true;
      }, 200);
    },

    updateProfileAndReloadIframe() {
      this.updateProfile();
      this.reloadIframe();
    },

    reloadIframe() {
      this.iframeLoaded = false;
      this.startLoadingAnimation();
      if (this.$refs.userIframe) {
        this.$refs.userIframe.src = this.qrUserUrl;
      }
    },
    initializeSocket() {
      this.socket = io(config.app_url, {
        transports: ['websocket'],
        timeout: 60000,
      });

      this.socket.on('connect', () => {
        console.log('Connected to server');
      });

      this.socket.on('disconnect', () => {
        console.log('Disconnected from server');
        this.reconnectSocket();
      });
      this.socket.on('upload-progress', (progress) => {
        this.uploadProgress = progress;
      });
      this.socket.on('upload-success', (data) => {
        this.handleUploadSuccess(data);
      });
      this.socket.on('upload-error', (error) => {
        this.handleUploadError(error);
      });
    },
    reconnectSocket() {
      if (!this.socket.connected) {
        setTimeout(() => {
          this.socket.connect();
        }, 1000);
      }
    },
    getCards() {
      axios.get(`${config.app_url}/user/get-cards`, {
        headers: { authorization: localStorage.getItem('authorization') }
      })
        .then(response => {
          if (response.data.success) {
            this.cards = response.data.msg.data;
          } else {
            this.notifyVue('top', 'right', "Veriler Alınamadı!", "warning");
          }
        })
        .catch(() => {
          router.push('login');
          this.notifyVue('top', 'right', "Bağlantı Hatası!", "warning");
        });
    },
    updateProfile() {
      axios.post(`${config.app_url}/user/update-user-card`,
        { cards_info: this.userCards },
        { headers: { authorization: localStorage.getItem('authorization') } }
      )
        .then(response => {
          if (response.data.success) {
            this.notifyVue('top', 'right', "Güncelleme Başarılı!", "success");
            this.reloadIframe();
          } else {
            this.notifyVue('top', 'right', "Güncelleme Başarısız!", "warning");
          }
        })
        .catch(() => {
          this.notifyVue('top', 'right', "Bağlantı Hatası!", "warning");
        });
    },
    getUser() {
      let app_data= this;
      axios.get(`${config.app_url}/user/get-user`, {
        headers: { authorization: localStorage.getItem('authorization') }
      })
        .then(response => {
          if (response.data.success) {
            this.setUserData(response.data.msg);
            app_data.qrUserUrl = `${config.qr_url}/qr-user/?hash=${response.data.msg.userHash}`;
          }
        })
        .catch(error => {
          router.push('login');
          this.$toast.error(error.toString());
        });
    },
    setUserData(userData) {
      this.user = userData;
      this.userCards = userData.cards_info || [];
      this.imageUrl = `${config.img_url}`;
      this.backgroundImageSrc = userData.background_img ? `${config.img_url}/${userData.background_img}` : "@/assets/img/background.jpg";
      this.profileImageSrc = userData.profile_img ? `${config.img_url}/${userData.profile_img}` : "@/assets/img/faces/face-2.jpg";
    },
    notifyVue(verticalAlign, horizontalAlign, message, type) {
      this.$notify({
        component: NotificationTemplate,
        title: message,
        icon: "info",
        horizontalAlign: horizontalAlign,
        verticalAlign: verticalAlign,
        type: type,
        duration: 2000
      });
    },
    getCardIconSrc(card) {
      if (card.name === 'custom') {
        return `${this.imageUrl}/icons/${card.file}`;
      }
      return `${this.imageUrl}/icons/${card.file}`;
    },
    handleCardSelection() {
      this.cardAddress = '';
      this.catalogFile = null;
      this.customIconFile = null;
    },

    handleCustomIconUpload(event) {
      const file = event.target.files[0];
      if (file) {
        if (file.size > this.maxImageSize) {
          this.notifyVue('top', 'right', "Dosya boyutu 5MB'dan küçük olmalıdır!", "warning");
          return;
        }
        this.customIconFile = file;
      }
    },
    uploadCustomIcon() {
      if (!this.customIconFile) {
        this.notifyVue('top', 'right', "Lütfen bir simge seçin!", "warning");
        return;
      }

      const formData = new FormData();
      formData.append('user_icon', this.customIconFile);

      this.isUploading = true;
      axios.post(`${config.app_url}/user/upload-icon`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          authorization: localStorage.getItem('authorization')
        }
      })
        .then(response => {
          if (response.data.success) {
            this.handleCustomIconUploadSuccess(response.data);
          } else {
            this.notifyVue('top', 'right', "Simge yüklenemedi!", "warning");
          }
        })
        .catch(() => {
          this.notifyVue('top', 'right', "Bağlantı Hatası!", "warning");
        })
        .finally(() => {
          this.isUploading = false;
        });
    },
    handleCustomIconUploadSuccess(data) {
      this.notifyVue('top', 'right', "Simge yüklendi!", "success");
      this.cardAddress = data.address;
      this.selectedCardItem.file = data.fileName;
      this.createNewCard();
    },
    removeCustomIcon() {
      this.customIconFile = null;
      this.cardAddress = '';
      if (this.editing_index !== null) {
        this.userCards[this.editing_index].file = null;
        this.userCards[this.editing_index].address = '';
      }
      this.notifyVue('top', 'right', "Özel simge kaldırıldı. Yeni bir simge yükleyebilirsiniz.", "success");
    },
    addOrUpdateCard() {
      if (!this.cardTitle) {
        this.notifyVue('top', 'right', "Lütfen bir başlık girin!", "warning");
        return;
      }

      if (this.selectedCardItem) {
        if (this.editing_index !== null) {
          // Düzenleme modu
          if (this.isCustomCard && this.customIconFile && this.customIconFile instanceof File) {
            // Yeni özel simge seçilmişse yükle
            this.uploadCustomIcon();
          } else if (this.isCatalogCard && this.catalogFile && this.catalogFile instanceof File) {
            // Yeni katalog seçilmişse yükle
            this.uploadCatalog();
          } else {
            // Sadece başlık güncellemesi
            this.updateCardTitle();
          }
        } else {
          // Yeni kart ekleme modu
          if (this.isCustomCard && this.customIconFile) {
            this.uploadCustomIcon();
          } else if (this.isCatalogCard && this.catalogFile) {
            this.uploadCatalog();
          } else {
            this.createNewCard();
          }
        }
      }
    },
    updateCardTitle() {
      const updatedCard = {
        ...this.userCards[this.editing_index],
        title: this.cardTitle,
        address:this.cardAddress,
      };
      if(this.selectedCardItem.name) updatedCard.name = this.selectedCardItem.name
      this.userCards[this.editing_index] = updatedCard;
      this.editing_index = null;
      this.resetCardForm();
      this.notifyVue('top', 'right', "Kart başlığı güncellendi!", "success");
    },
    createNewCard() {
      const newCard = {
        name: this.selectedCardItem.name,
        title: this.cardTitle,
        address: this.cardAddress,
        file: this.selectedCardItem.file,
        status: true
      };

      if (this.editing_index !== null) {
        this.userCards[this.editing_index] = newCard;
        this.editing_index = null;
      } else {
        this.userCards.push(newCard);
      }

      this.resetCardForm();
      this.notifyVue('top', 'right', "Kart başarıyla eklendi!", "success");
    },
    resetCardForm() {
      this.cardTitle = null;
      this.cardAddress = null;
      this.selectedCardItem = null;
      this.customIconFile = null;
      this.catalogFile = null;
      this.isUploading = false;
      this.uploadProgress = 0;
    },
    handleCatalogSelection(event) {
      const file = event.target.files[0];
      if (file) {
        if (file.size > 50 * 1024 * 1024) { // 50MB
          this.notifyVue('top', 'right', "Dosya boyutu 50MB'dan küçük olmalıdır!", "warning");
          return;
        }
        this.catalogFile = file;
      }
    },
    uploadCatalog() {
      if (!this.catalogFile) {
        this.notifyVue('top', 'right', "Lütfen bir katalog dosyası seçin!", "warning");
        return;
      }

      this.isUploading = true;
      this.uploadProgress = 0;
      this.uploadFileInChunks(this.catalogFile);
    },
    uploadFileInChunks(file) {
      const chunkSize = 256 * 1024; // 256KB chunks
      let offset = 0;
      this.socket.emit('start-upload', { fileName: file.name, fileSize: file.size });

      const sendChunk = () => {
        const reader = new FileReader();
        const blob = file.slice(offset, offset + chunkSize);
        reader.onload = (e) => {
          this.socket.emit('upload-chunk', {
            fileName: file.name,
            data: new Uint8Array(e.target.result)
          });

          offset += e.target.result.byteLength;
          if (offset < file.size) {
            setTimeout(sendChunk, 50); // Add a small delay between chunks
          } else {
            this.socket.emit('end-upload');
          }
        };
        reader.readAsArrayBuffer(blob);
      };

      sendChunk();
    },
    handleUploadSuccess(data) {
      this.isUploading = false;
      this.uploadProgress = 100;
      this.cardAddress = `${config.app_url}/uploads/catalog/${data.fileName}`;
      this.notifyVue('top', 'right', "Katalog yükleme tamamlandı!", "success");
      this.createNewCard();
    },
    handleUploadError(error) {
      this.isUploading = false;
      this.notifyVue('top', 'right', `Yükleme hatası: ${error}`, "error");
    },

    editCards(index) {
      const card = this.userCards[index];
      this.editing_index = index;
      this.cardTitle = card.title;
      this.cardAddress = card.address;
      this.selectedCardItem = this.cards.find(x => x.file === card.file) || { name: 'custom', file: card.file };

      if (this.isCatalogCard) {
        this.catalogFile = card.file ? { name: card.file } : null;
      } else if (this.isCustomCard) {
        this.customIconFile = card.file ? { name: card.file } : null;
      }
    },

    removeCatalog() {
      this.catalogFile = null;
      this.uploadProgress = 0;
      this.cardAddress = '';
      this.isUploading = false;
      if (this.editing_index !== null) {
        this.userCards[this.editing_index].file = null;
        this.userCards[this.editing_index].address = '';
      }
      this.notifyVue('top', 'right', "Katalog dosyası kaldırıldı. Yeni bir katalog yükleyebilirsiniz.", "success");
    },
    removeCards(index) {
      this.userCards.splice(index, 1);
    },
  },
};
</script>

<style>
.iphone-x {
  width: 350px;
  height: 700px;
  max-width: 375px;
  max-height: 812px;
  background: #000;
  position: relative;
  border: 16px solid #222;
  border-radius: 44px;
  overflow: hidden;
}

.iframe-container {
  width: 100%;
  height: 100%;
}

iframe {
  width: 100%;
  height: 100%;
  border: none;
}

.progress-container {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 80%;
}

@media (max-width: 767.98px) {
  .col-xl-3, .col-lg-3, .col-md-6, .col-sm-12,
  .col-xl-4, .col-lg-4, .col-md-6, .col-sm-12,
  .col-xl-1, .col-lg-1, .col-md-12, .col-sm-12 {
    flex: 0 0 100%;
    max-width: 100%;
  }

  .d-flex.align-items-end {
    justify-content: center;
    margin-top: 10px;
  }

  .card .tableClass {
    overflow-x: auto;
    display: block;
    white-space: nowrap;
  }
}
</style>
